export interface TimeZoneOption {
  value: string
  label: string
}

/**
 * Maps legacy timezone display labels (stored by the backend) to IANA timezone identifiers.
 * The legacy admin stored the select option text as the value, e.g., "Mountain Time (US & Canada)".
 * Values come from Rails ActiveSupport::TimeZone::MAPPING.
 */
export const LEGACY_TIMEZONE_MAP: Record<string, string> = {
  // Rails ActiveSupport maps "Alaska" → "America/Juneau". The two zones
  // (Juneau vs Anchorage) have been identical since 1983, so this won't
  // affect displayed times for any existing data.
  Alaska: 'America/Juneau',
  Arizona: 'America/Phoenix',
  'Central Time (US & Canada)': 'America/Chicago',
  'Eastern Time (US & Canada)': 'America/New_York',
  'Greenwich Mean Time (UK)': 'Europe/London',
  Hawaii: 'Pacific/Honolulu',
  'Indiana (East)': 'America/Indiana/Indianapolis',
  'Mountain Time (US & Canada)': 'America/Denver',
  'Pacific Time (US & Canada)': 'America/Los_Angeles',
  'Puerto Rico': 'America/Puerto_Rico',
  'Atlantic Time (Canada)': 'America/Halifax',
  London: 'Europe/London',
  Edinburgh: 'Europe/London',
  Dublin: 'Europe/Dublin',
  Lisbon: 'Europe/Lisbon',
  Saskatchewan: 'America/Regina',
  Guadalajara: 'America/Mexico_City',
  'Mexico City': 'America/Mexico_City',
  Monterrey: 'America/Monterrey',
  'Central America': 'America/Guatemala',
  Bogota: 'America/Bogota',
  Lima: 'America/Lima',
  Quito: 'America/Lima',
  Caracas: 'America/Caracas',
  'La Paz': 'America/La_Paz',
  Santiago: 'America/Santiago',
  Newfoundland: 'America/St_Johns',
  Brasilia: 'America/Sao_Paulo',
  'Buenos Aires': 'America/Argentina/Buenos_Aires',
  Georgetown: 'America/Guyana',
  Greenland: 'America/Nuuk',
  UTC: 'Etc/UTC',
}

/**
 * Normalizes a timezone value from the backend to an IANA identifier.
 * Handles legacy display-label values (e.g., "Mountain Time (US & Canada)" -> "America/Denver")
 * and passes through values that are already IANA identifiers.
 */
export function normalizeTimeZone(tz: string): string {
  return LEGACY_TIMEZONE_MAP[tz] ?? tz
}

export const CURATED_TIMEZONE_OPTIONS: TimeZoneOption[] = [
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  {
    value: 'America/Vancouver',
    label: 'Pacific Time - British Columbia (PCT)',
  },
  { value: 'America/Phoenix', label: 'Arizona, Yukon Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Indiana/Indianapolis', label: 'Indiana (East) Time' },
  { value: 'America/St_Johns', label: 'Newfoundland Time' },
]

/** Sorted by UTC offset, west to east. Labels match GET /time-zones/ API response. */
export const EXTENDED_TIMEZONE_OPTIONS: TimeZoneOption[] = [
  { value: 'America/Adak', label: 'America/Adak' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'America/Juneau', label: 'America/Juneau' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  {
    value: 'America/Vancouver',
    label: 'Pacific Time - British Columbia (PCT)',
  },
  { value: 'America/Santa_Isabel', label: 'America/Santa Isabel' },
  { value: 'America/Phoenix', label: 'Arizona, Yukon Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Mazatlan', label: 'America/Mazatlan' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Mexico_City', label: 'Mexico City Time' },
  {
    value: 'America/Regina',
    label: 'Saskatchewan, Guatemala, Costa Rica Time',
  },
  { value: 'America/Atikokan', label: 'America/Atikokan' },
  { value: 'America/Bogota', label: 'Bogota, Jamaica, Lima Time' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Indiana/Indianapolis', label: 'Indiana (East) Time' },
  { value: 'America/Havana', label: 'America/Havana' },
  { value: 'America/Caracas', label: 'Caracas Time' },
  { value: 'America/Puerto_Rico', label: 'Puerto Rico' },
  { value: 'America/Halifax', label: 'Atlantic Time' },
  { value: 'America/Moncton', label: 'Atlantic Daylight Time' },
  { value: 'America/Campo_Grande', label: 'America/Campo Grande' },
  { value: 'America/Asuncion', label: 'Asuncion Time' },
  { value: 'America/Santiago', label: 'Santiago Time' },
  { value: 'America/St_Johns', label: 'Newfoundland Time' },
  { value: 'America/Sao_Paulo', label: 'Brasilia Time' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires Time' },
  { value: 'America/Montevideo', label: 'Montevideo Time' },
  { value: 'America/Miquelon', label: 'America/Miquelon' },
  { value: 'America/Godthab', label: 'America/Godthab' },
  { value: 'America/Noronha', label: 'America/Noronha' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (UK)' },
]

/**
 * Maps IANA identifiers to the friendly label Mango uses in its timezone
 * dropdowns. Built lazily from EXTENDED_TIMEZONE_OPTIONS (a superset of the
 * curated list) so the two never drift apart.
 */
let ianaToLabelCache: Map<string, string> | null = null

const getIanaToLabelMap = (): Map<string, string> => {
  if (!ianaToLabelCache) {
    ianaToLabelCache = new Map(
      EXTENDED_TIMEZONE_OPTIONS.map((option) => [option.value, option.label]),
    )
  }
  return ianaToLabelCache
}

/**
 * Resolve a human-readable label for a timezone value coming from the backend.
 *
 * Prefers Mango's curated label (e.g. `America/Phoenix` -> "Arizona, Yukon
 * Time") so display matches the option users select. Falls back to the
 * platform `Intl` long name, then the raw value.
 *
 * This avoids the `Intl`-only behaviour where `America/Phoenix` renders as
 * "Mountain Standard Time" because Phoenix shares the Mountain zone but never
 * observes daylight saving.
 */
export function getTimeZoneLabel(tz: string): string {
  if (!tz) return tz

  const normalized = normalizeTimeZone(tz)
  const curatedLabel = getIanaToLabelMap().get(normalized)
  if (curatedLabel) return curatedLabel

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: normalized,
      timeZoneName: 'long',
    })
    const timeZonePart = formatter
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')
    if (timeZonePart?.value) return timeZonePart.value
  } catch {
    // Unknown/invalid IANA identifier — fall through to a best-effort label.
  }

  return normalized.split('/').pop()?.replace(/_/g, ' ') || normalized
}
